import {
  DemandeAffiliationRefuserGQL,
  DemandeAffiliationsAffecterGQL,
  DemandeAffiliationSortInput,
  SignatureRelancerGQL,
  SortEnumType,
} from '@graphql/generated';
import {
  GetDemandeAffiliationsCountByStatutBackOfficeGQL,
  GetDemandeAffiliationsFilteredBackOfficeGQL,
  DemandeAffiliationStatutBackOfficeEnum,
  DemandeAffiliation as DemandeAffiliationGql,
} from '@graphql/generated';
import { inject, Injectable } from '@angular/core';
import { DemandeAffiliationBackOfficeService } from '../demande-affiliation-backoffice.service';
import { DemandeAffiliation } from '@lib/models/demande-affiliation/demande-affiliation.model';
import { DemandeAffiliationStatutBackOffice } from '@lib/models/demande-affiliation/types/demande-statut.type';
import { Pagination } from '@lib/models/pagination.model';
import { combineLatest, lastValueFrom, map, Observable } from 'rxjs';
import { BackOfficeTabsCounters } from '../../models/backoffice-tabs-counters.model';
import { StatutBackOfficeMapper } from '@lib/mappers/graphql/statut-backoffice-mapper';
import { DemandeAffiliationMapper } from '@lib/mappers/graphql/demande-affiliation-mapper';

@Injectable({ providedIn: 'root' })
export class GraphQlDemandeAffiliationBackofficeService extends DemandeAffiliationBackOfficeService {
  private readonly getDemandeAffiliationCountGql = inject(
    GetDemandeAffiliationsCountByStatutBackOfficeGQL
  );

  private readonly getDemandesAffiliationGql = inject(
    GetDemandeAffiliationsFilteredBackOfficeGQL
  );

  private readonly demandeAffiliationsAffecterGql = inject(
    DemandeAffiliationsAffecterGQL
  );

  private readonly demandeAffiliationRefuserGql = inject(DemandeAffiliationRefuserGQL);

  private readonly signatureRelancerGql = inject(SignatureRelancerGQL);

  private readonly aAffecterStatutsGql: DemandeAffiliationStatutBackOfficeEnum[] =
    this.aAffecterStatuts.map(s => StatutBackOfficeMapper.toGraphQl(s)!);

  private readonly aControlerGql: DemandeAffiliationStatutBackOfficeEnum[] =
    this.aControlerStatuts.map(s => StatutBackOfficeMapper.toGraphQl(s)!);

  private readonly enAttenteGql: DemandeAffiliationStatutBackOfficeEnum[] =
    this.enAttenteStatuts.map(s => StatutBackOfficeMapper.toGraphQl(s)!);

  private readonly enSignatureStatutsGql: DemandeAffiliationStatutBackOfficeEnum[] =
    this.enSignatureStatuts.map(s => StatutBackOfficeMapper.toGraphQl(s)!);

  private readonly queryRefAAffecter = this.getDemandeAffiliationCountGql.watch({
    variables: { statuts: this.aAffecterStatutsGql },
    ...{ fetchPolicy: 'network-only' },
  });

  private readonly queryRefAControler = this.getDemandeAffiliationCountGql.watch({
    variables: { statuts: this.aControlerGql },
    ...{ fetchPolicy: 'network-only' },
  });

  private readonly queryRefEnAttente = this.getDemandeAffiliationCountGql.watch({
    variables: { statuts: this.enAttenteGql },
    ...{ fetchPolicy: 'network-only' },
  });

  private readonly queryRefEnSignature = this.getDemandeAffiliationCountGql.watch({
    variables: { statuts: this.enSignatureStatutsGql },
    ...{ fetchPolicy: 'network-only' },
  });

  private aAffecter$ = this.queryRefAAffecter.valueChanges.pipe(
    map(result => result.data?.demandeAffiliationCountByStatutBackOffice)
  );

  private aControler$ = this.queryRefAControler.valueChanges.pipe(
    map(result => result.data?.demandeAffiliationCountByStatutBackOffice)
  );

  private enAttente$ = this.queryRefEnAttente.valueChanges.pipe(
    map(result => result.data?.demandeAffiliationCountByStatutBackOffice)
  );

  private enSignature$ = this.queryRefEnSignature.valueChanges.pipe(
    map(result => result.data?.demandeAffiliationCountByStatutBackOffice)
  );

  public override watchDemandesCountByCategories(): Observable<BackOfficeTabsCounters> {
    const interval = 15000;

    this.queryRefAAffecter.startPolling(interval);
    this.queryRefAControler.startPolling(interval);
    this.queryRefEnAttente.startPolling(interval);
    this.queryRefEnSignature.startPolling(interval);

    return combineLatest([
      this.aAffecter$,
      this.aControler$,
      this.enAttente$,
      this.enSignature$,
    ]).pipe(
      map(
        ([aAffecter, aControler, enAttente, enSignature]) =>
          ({
            aAffecter: aAffecter,
            aControler: aControler,
            enAttente: enAttente,
            enSignature: enSignature,
          }) as BackOfficeTabsCounters
      )
    );
  }

  private updateCountByCategories(): void {
    this.queryRefAAffecter.refetch();
    this.queryRefAControler.refetch();
    this.queryRefEnAttente.refetch();
    this.queryRefEnSignature.refetch();
  }

  public override async getDemandeAffiliations(
    search: string | null,
    statuts: DemandeAffiliationStatutBackOffice[],
    gestionnaireCodes: string[],
    pageIndex: number,
    pageSize: number,
    sortField: 'dateDeclaration' | 'dateUpdate',
    sortOrder: 'asc' | 'desc'
  ): Promise<Pagination<DemandeAffiliation>> {
    const statutsGql = statuts.map(s => StatutBackOfficeMapper.toGraphQl(s)!);

    const sortTypeGql: SortEnumType =
      sortOrder === 'asc' ? SortEnumType.Asc : SortEnumType.Desc;

    const sortGql: DemandeAffiliationSortInput[] =
      sortField === 'dateDeclaration'
        ? [{ dateDeclaration: sortTypeGql }, { dateUpdate: SortEnumType.Desc }]
        : [{ dateUpdate: sortTypeGql }, { dateDeclaration: SortEnumType.Desc }];

    const queryResult = await lastValueFrom(
      this.getDemandesAffiliationGql.fetch({
        variables: {
          take: pageSize,
          skip: pageSize * pageIndex,
          search: search,
          statuts: statutsGql,
          order: sortGql,
          gestionnaireCodes: gestionnaireCodes,
        },
        ...{ fetchPolicy: 'network-only' },
      })
    );

    const data = queryResult.data!.demandeAffiliationFilteredBackOffice;

    const demandesAffiliaiton: DemandeAffiliation[] =
      data?.items?.map(i =>
        DemandeAffiliationMapper.fromGraphQL(i as DemandeAffiliationGql, true)
      ) ?? [];

    return new Pagination<DemandeAffiliation>(
      demandesAffiliaiton,
      pageIndex,
      pageSize,
      data!.totalCount
    );
  }

  public override async affecterDemandeAffiliations(
    publicIds: string[],
    gestionnaireId: string
  ): Promise<void> {
    await lastValueFrom(
      this.demandeAffiliationsAffecterGql.mutate({
        variables: {
          gestionnaireId: gestionnaireId,
          demandeAffiliationPublicIds: publicIds,
        },
      })
    );
    this.updateCountByCategories();
  }

  public override async refuserDemandeAffiliation(
    publicId: string,
    commentaire: string
  ): Promise<void> {
    await lastValueFrom(
      this.demandeAffiliationRefuserGql.mutate({
        variables: {
          publicId: publicId,
          commentaire: commentaire,
        },
      })
    );
    this.updateCountByCategories();
  }

  public override async relancerSignature(
    publicId: string,
    newEmail: string | null,
    useNewEmailAsDefault: boolean,
    newNumber: string | null,
    useNewNumberAsDefault: boolean
  ): Promise<void> {
    await lastValueFrom(
      this.signatureRelancerGql.mutate({
        variables: {
          publicId: publicId,
          newEmail: newEmail,
          useNewEmailAsDefault: useNewEmailAsDefault,
          newNumber: newNumber,
          useNewNumberAsDefault: useNewNumberAsDefault,
        },
      })
    );
  }
}
